// src/pages/SupplierInvoicesList.js
// Supplier-facing list of settlement invoices (read-only).
// Mounted at /supplier/invoices — the sidebar's "Mes factures".
//
// Was hand-rolled inline styles: a white table with hard-coded grey headers,
// dropped as a light slab into the ink dashboard. Now on the shared chrome/ink
// primitives, so it matches every other supplier page and the row colours come
// from the palette instead of being invented here.
//
// NOTE: Authorization comes from the global axios interceptor in App.js.

import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api/baseURL';
import { Page, Panel, Table, Tr, Td, Badge, EmptyState, Loading, ErrorNote } from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

function useInvoices() {
  const [state, setState] = React.useState({ loading: true, error: '', data: [] });

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setState({ loading: true, error: '', data: [] });
      try {
        const res = await axios.get(`${API_URL}/quotations/supplier-invoices/supplier`);
        // The endpoint has returned a bare array in some versions and
        // { items: [...] } in others — accept either rather than showing none.
        const arr = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.items)
            ? res.data.items
            : [];
        if (alive) setState({ loading: false, error: '', data: arr });
      } catch (e) {
        if (!alive) return;
        const msg = e?.response?.data?.error || e.message || 'Chargement des factures impossible';
        setState({ loading: false, error: msg, data: [] });
      }
    })();
    return () => { alive = false; };
  }, []);

  return state;
}

const money = (v) => `${fmt(v)} FCFA`;

const fmtDate = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? '—'
    : t.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Status carries the only colour on the row — the dashboard's two-signal rule.
const STATUS = {
  paid: { label: 'Payée', tone: 'ok' },
  awaiting_payment: { label: 'En attente', tone: 'warn' },
  cancelled: { label: 'Annulée', tone: 'danger' },
  overdue: { label: 'En retard', tone: 'danger' },
};
const statusOf = (s) => STATUS[s] || { label: String(s || 'inconnu').replace(/_/g, ' '), tone: 'neutral' };

const R = ({ children }) => <span className="block text-right">{children}</span>;

export default function SupplierInvoicesList() {
  const { loading, error, data } = useInvoices();
  const navigate = useNavigate();

  const rows = Array.isArray(data) ? data : [];
  const unpaid = rows.filter((r) => r?.status === 'awaiting_payment').length;

  return (
    <Page
      subtitle="Documents"
      title="Mes factures"
    >
      <ErrorNote>{error}</ErrorNote>

      <Panel
        title="Factures fournisseur"
        action={
          unpaid > 0 ? (
            <Badge tone="warn">{unpaid} en attente</Badge>
          ) : null
        }
        bodyClass="p-0"
      >
        {loading ? (
          <Loading label="Chargement des factures…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Aucune facture pour le moment"
            hint="Lorsqu'un acheteur accepte votre devis, la facture correspondante apparaît ici."
          />
        ) : (
          <Table
            minWidth={980}
            head={[
              'Créée',
              'Statut',
              'Acheteur',
              'Véhicule',
              <R key="st">Sous-total</R>,
              <R key="tva">TVA</R>,
              <R key="tot">Total</R>,
              <R key="fee">Frais plateforme</R>,
            ]}
          >
            {rows.map((r, idx) => {
              const id = r?._id || r?.id || String(idx);
              const buyer = r?.buyer || {};
              const veh = r?.vehicle || {};
              const totals = r?.totals || {};
              const fee = r?.platformFee || {};
              const st = statusOf(r?.status);
              const buyerName =
                buyer?.name || buyer?.fullName || buyer?.businessName || buyer?.email || '—';
              const vehLabel = [
                [veh?.brand, veh?.model].filter(Boolean).join(' '),
                veh?.year ? `(${veh.year})` : '',
                veh?.plate ? `• ${veh.plate}` : '',
              ].filter(Boolean).join(' ') || '—';

              return (
                <Tr key={id} onClick={() => navigate(`/supplier/invoices/${id}`)}>
                  <Td>{fmtDate(r?.createdAt)}</Td>
                  <Td><Badge tone={st.tone}>{st.label}</Badge></Td>
                  <Td>
                    <span className="text-[#F4F1EA]">{buyerName}</span>
                    {r?.buyerType && (
                      <span className="ml-1.5 text-[11px] text-[#6E665C]">({r.buyerType})</span>
                    )}
                  </Td>
                  <Td>{vehLabel}</Td>
                  <Td mono className="text-right">{money(totals?.subtotalPublic)}</Td>
                  <Td mono className="text-right">{money(totals?.taxPublic)}</Td>
                  <Td mono className="text-right font-semibold">{money(totals?.totalPublic)}</Td>
                  <Td mono className="text-right">{money(fee?.expected)}</Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </Page>
  );
}
